import { Dispatch, SetStateAction } from 'react';
import * as Sentry from '@sentry/react';
import Icon from '../Icon';
import { IconSize, Icons } from '@/enums';
import { ColumnType, SalesRefTableType, AdsTableType} from '@/types/dataTypes';
import { defaultSoldTableColumns } from '@/context/SoldDefault';

interface SortingType {
  id: string;
  desc: boolean;
}

const toggleRowSelection = (
  rowId: string,
  setRowSelection: Dispatch<
    SetStateAction<{
      [key: string]: boolean;
    }>
  >
) => {
  setRowSelection((prev) => ({
    ...prev,
    [rowId]: !prev[rowId],
  }));
};

const saveSelectedColumnOptions = (columns: ColumnType[]) => {
  localStorage.setItem(
    'vg-col-ownChoice',
    JSON.stringify(columns)
  );
};

const getSelectedColumnOptions = (): ColumnType[] => {
  const userStr = localStorage.getItem('vg-col-ownChoice');
  if (userStr) {
    try {
      return JSON.parse(userStr);
    } catch (ex) {
      Sentry.captureException(ex);
      return defaultSoldTableColumns; // or handle the error as appropriate
    }
  }
  return defaultSoldTableColumns; // Return empty array if userStr is null
};

const ROW_REMOVE_MS = 280;

// Fade/slide the row out before the caller removes it from the data, so hidden
// rows disappear with a small animation instead of vanishing instantly.
const animateRowRemoval = (element: HTMLElement, onRemoved: () => void) => {
  const row = element.closest('tr');
  if (!row) {
    onRemoved();
    return;
  }
  row.style.transition = `opacity ${ROW_REMOVE_MS}ms ease, transform ${ROW_REMOVE_MS}ms ease`;
  row.style.opacity = '0';
  row.style.transform = 'translateX(16px)';
  row.style.pointerEvents = 'none';
  setTimeout(onRemoved, ROW_REMOVE_MS);
};

const handleColumnClick = (
  setSorting: Dispatch<SetStateAction<SortingType[]>>,
  columnId?: string
) => {
  if (columnId) {
    setSorting((prevSorting) => {
      const existingSort = prevSorting.find((sort) => sort.id === columnId);
      if (existingSort) {
        const newSorting = prevSorting.map((sort) =>
          sort.id === columnId ? { ...sort, desc: !sort.desc } : sort
        );
        return newSorting;
      } else {
        const newSorting = [{ id: columnId, desc: false }];
        return newSorting;
      }
    });
  }
};

const renderSortingIcon = (sorting: SortingType[], columnId?: string) => {
  const sort = sorting.find((sort) => sort.id === columnId);
  if (!sort) return null;
  return sort.desc ? (
    <Icon icon={Icons.chevronDown} size={IconSize.small} />
  ) : (
    <Icon icon={Icons.chevronUp} size={IconSize.small} />
  );
};

const replaceFirstCommaWithDot = (input?: string | null): string => {
  if (input == null) return '';
  return input.replace(',', '.');
};

const calculatePriceTaxationRatioAverage = (data: any[] = [], field: any): string | null => {
  if (!data || data.length === 0) return null;
  const validValues = data
    .map((item) => item[field])
    .map((value) => {
      if (value == null) return NaN;
      return Number(replaceFirstCommaWithDot(String(value)));
    })
    .filter((value) => value != null && !isNaN(value));

  if (validValues.length === 0) return null;
  const avg = validValues.reduce((sum, value) => sum + value, 0) / validValues.length;
  return avg.toFixed(2).replace('.', ',');
};

const calculateAverageGeneric = <T extends Record<string, any>>(
  data: T[] | null, 
  field: keyof T
): number | null => {
  if (!data || data.length === 0) return null;
  
  const validValues = data
    .map((item) => item[field])
    .map((value) => Number(value))
    .filter((value) => value != null && !isNaN(Number(value)))
  
  if (validValues.length === 0) return null;
  return removeDecimals(validValues.reduce((sum, value) => sum + value, 0) / validValues.length);
};

const normalizeDate = (date: any): number | null => {
  if (date instanceof Date) {
    return date.getTime();
  } else if (typeof date === 'string') {
    const parsedDate = new Date(date);
    return isNaN(parsedDate.getTime()) ? null : parsedDate.getTime();
  } else if (typeof date === 'number') {
    return date < 1e10 ? date * 1000 : date;
  }
  return null;
};

const removeDecimals = (num: number | null): number | null => {
  return num !== null ? Math.round(num) : null;
};

const calculateAverageMonthlyFeeSqm = (data: AdsTableType[] | null): number => {
  if (!data || data.length === 0) return 0;
  
  const validValues = data
    .filter(item => item.monthlyFee != null && item.livingArea != null && item.livingArea > 0)
    .map(item => item.monthlyFee / item.livingArea);
  
  if (validValues.length === 0) return 0;
  const average = validValues.reduce((sum, value) => sum + value, 0) / validValues.length;
  return removeDecimals(average) ?? 0;
};

const calculateAverageDateTimestamp = <T extends SalesRefTableType | AdsTableType>(
  data: T[] | null, 
  field: keyof T
): string => {
  if (!data || data.length === 0) return '';
  
  const validDates = data
    .map((item) => item[field])
    .filter((value) => value != null)
    .map(normalizeDate)
    .filter((time) => time !== null) as number[];
  
  if (validDates.length === 0) return '';
  const averageTime = validDates.reduce((sum, time) => sum + time, 0) / validDates.length;
  return new Date(averageTime).toISOString().split('T')[0];
};

export {
  toggleRowSelection,
  animateRowRemoval,
  handleColumnClick,
  renderSortingIcon,
  getSelectedColumnOptions,
  saveSelectedColumnOptions,
  calculateAverageMonthlyFeeSqm,
  calculateAverageDateTimestamp,
  removeDecimals,
  calculateAverageGeneric,
  normalizeDate,
  calculatePriceTaxationRatioAverage,
  replaceFirstCommaWithDot
};
export type { SortingType };
